// Design tokens for NPSP Components. Never put raw hex values in components;
// always reference these tokens. See docs/NPSP-COMPONENTS-BLUEPRINT.md §6.

export const colors = {
  brand: '#0176d3',
  brandHover: '#014486',
  brandMuted: '#e3f1fc',

  success: '#2e844a',
  warning: '#fe9339',
  error: '#c23934',
  info: '#5aa9e6',

  engagement: {
    cold: '#6b7a8f',
    luke: '#5aa9e6',
    warm: '#f2a93b',
    hot: '#f97316',
    onFire: '#dc2626',
  },

  score: {
    low: '#c23934',
    mid: '#fe9339',
    high: '#2e844a',
  },

  moves: {
    id: '#6b7a8f',
    qual: '#5aa9e6',
    cult: '#9050e9',
    solicit: '#f97316',
    stw: '#2e844a',
  },

  timeline: {
    gift: '#2e844a',
    email: '#5aa9e6',
    call: '#9050e9',
    task: '#fe9339',
    note: '#6b7a8f',
    event: '#06a59a',
    web: '#b8b5b3',
    volunteer: '#f97316',
    system: '#b8b5b3',
  },

  status: {
    major:     { bg: '#fdecd8', fg: '#a05000' },
    monthly:   { bg: '#d8f0e0', fg: '#0b5d1e' },
    board:     { bg: '#e9dcfb', fg: '#5b21b6' },
    volunteer: { bg: '#fce7e7', fg: '#7f1d1d' },
    alumni:    { bg: '#dcedfc', fg: '#1e40af' },
    lapsed:    { bg: '#f3f3f3', fg: '#3e3e3c' },
    deceased:  { bg: '#1a1a1a', fg: '#ffffff' },
  },

  surface: '#ffffff',
  surfaceAlt: '#f7f7f8',
  surfaceInk: '#f3f3f3',
  overlay: 'rgba(0,0,0,0.4)',

  border: '#dddbda',
  borderStrong: '#c9c7c5',
  textPrimary: '#080707',
  textSecondary: '#444444',
  textTertiary: '#706e6b',
  textInverse: '#ffffff',
};

export const space = [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64];

export const radius = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  pill: 999,
};

export const type = {
  display: { fontSize: 32, fontWeight: 300, lineHeight: 1.2 },
  h1:      { fontSize: 24, fontWeight: 400, lineHeight: 1.25 },
  h2:      { fontSize: 20, fontWeight: 600, lineHeight: 1.3 },
  h3:      { fontSize: 16, fontWeight: 700, lineHeight: 1.35 },
  body:    { fontSize: 14, fontWeight: 400, lineHeight: 1.5 },
  small:   { fontSize: 13, fontWeight: 400, lineHeight: 1.45 },
  micro:   { fontSize: 12, fontWeight: 400, lineHeight: 1.4 },
  label:   { fontSize: 11, fontWeight: 600, lineHeight: 1.3, letterSpacing: 0.5, textTransform: 'uppercase' },
  kpi:     { fontSize: 28, fontWeight: 700, lineHeight: 1.1 },
  kpiUnit: { fontSize: 13, fontWeight: 400, lineHeight: 1.3 },
};

export const elevation = {
  none: 'none',
  card: '0 1px 2px rgba(0,0,0,0.06)',
  cardHover: '0 4px 12px rgba(0,0,0,0.08)',
  sticky: '0 2px 4px rgba(0,0,0,0.08)',
  drawer: '-8px 0 24px rgba(0,0,0,0.12)',
  modal: '0 16px 48px rgba(0,0,0,0.24)',
};

export const motion = {
  fast:   '120ms cubic-bezier(0.4, 0, 0.2, 1)',
  base:   '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow:   '320ms cubic-bezier(0.4, 0, 0.2, 1)',
  spring: '400ms cubic-bezier(0.2, 0.9, 0.3, 1.1)',
};

export const layout = {
  heroHeight: 140,
  kpiStripHeight: 92,
  leftRailWidth: 260,
  rightRailWidth: 300,
  maxContentWidth: 1400,
};

// Helpers
export const engagementLevel = (score) => {
  if (score >= 80) return { label: 'On Fire', color: colors.engagement.onFire, step: 4 };
  if (score >= 65) return { label: 'Hot', color: colors.engagement.hot, step: 3 };
  if (score >= 45) return { label: 'Warm', color: colors.engagement.warm, step: 2 };
  if (score >= 25) return { label: 'Luke-warm', color: colors.engagement.luke, step: 1 };
  return { label: 'Cold', color: colors.engagement.cold, step: 0 };
};

export const scoreColor = (score) => {
  if (score >= 70) return colors.score.high;
  if (score >= 40) return colors.score.mid;
  return colors.score.low;
};

export const formatMoney = (n, { cents = false, compact = false } = {}) => {
  if (n == null) return '—';
  if (compact && Math.abs(n) >= 1000) {
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  }
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  })}`;
};

export const formatDateShort = (d) => {
  if (!d) return '—';
  const dt = typeof d === 'string' ? new Date(d) : d;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const daysSince = (d) => {
  if (!d) return null;
  const dt = typeof d === 'string' ? new Date(d) : d;
  return Math.floor((Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24));
};

export const relativeDate = (d) => {
  const days = daysSince(d);
  if (days == null) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
};
